import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackClick } from "@/hooks/useAnalytics";
import { formatMonthYear } from "@/lib/date";

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  category: string | null;
  excerpt: string | null;
  image_url: string | null;
  published_at: string | null;
  created_at: string;
}

export const News = () => {
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const fetchNews = async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("id, title, slug, category, excerpt, image_url, published_at, created_at")
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(4);

      if (!error && data) {
        setNews(data);
      }
      setLoading(false);
    };

    fetchNews();
  }, []);




  return (
    <section id="noticias" className="py-12 lg:py-16 bg-secondary/30" ref={containerRef}>
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 lg:mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-6"
        >
          <div>
            <h2 className="font-display text-4xl lg:text-6xl xl:text-7xl font-light text-foreground">
              Notícias & Artigos
            </h2>
          </div>
          <Link
            to="/noticias"
            className="inline-flex items-center gap-2 text-primary font-medium hover:gap-4 transition-all"
          >
            Ver todas as notícias & artigos
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* News Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card border border-border rounded-sm p-6 lg:p-8 animate-pulse">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-4 h-4 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded w-1/4" />
                </div>
                <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                <div className="h-4 bg-muted rounded w-full mb-2" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Nenhuma notícia disponível no momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {news.map((item, index) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  delay: index * 0.1,
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group"
              >
                <Link to={`/noticias/${item.slug}`} onClick={() => trackClick("News", item.title)}>
                  <div className="relative bg-card border border-border rounded-sm p-6 lg:p-8 h-full hover:border-primary/50 transition-all duration-500 card-hover">
                    {/* Date */}
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4">
                      <Calendar className="w-4 h-4" />
                      {formatMonthYear(item.published_at || item.created_at)}
                    </div>

                    {/* Title */}
                    <h3 className="font-display text-xl lg:text-2xl text-foreground mb-3 group-hover:text-primary transition-colors duration-300 leading-tight">
                      {item.title}
                    </h3>

                    {/* Excerpt */}
                    {item.excerpt && (
                      <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                        {item.excerpt}
                      </p>
                    )}

                    {/* Read More */}
                    <motion.span
                      className="inline-flex items-center gap-2 text-primary text-sm font-medium"
                      whileHover={{ x: 5 }}
                    >
                      Saiba mais
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.span>

                    {/* Hover Line */}
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-500" />
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
